import { useCallback, useState } from 'react';

import { adminLocalizationApi, adminLocalizationKeys } from '@/api';
import { ConstructorApi } from '@/features/constructorProducts/api';
import { modifyAnswers } from '@/features/constructorCommon/utils';
import { queryClient } from '@/app/QueryClientProvider';
import { usePopups } from '@/hooks';

export const useProductQuestion = ({ productId }) => {
  const { showErrorToast, showSuccessToast } = usePopups();
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCreate = useCallback(async ({ question, groupId, dAnswersProp }) => {
    try {
      setIsCreating(true);
      const visibleConditions = dAnswersProp
        ? modifyAnswers(dAnswersProp)
        : question.visibleConditions;
      const result = await ConstructorApi.createQuestion(
        { ...question, visibleConditions },
        groupId
      );
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getProductsSections],
          queryFn: () => adminLocalizationApi.getQuestionnaireSections({ productId: productId })
        });
        showSuccessToast('Created');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsCreating(false);
    }
  }, []);

  const handleUpdate = useCallback(async ({ question, dAnswersProp }) => {
    try {
      setIsUpdating(true);
      const visibleConditions = dAnswersProp
        ? modifyAnswers(dAnswersProp)
        : question.visibleConditions;
      const result = await ConstructorApi.updateQuestion({ ...question, visibleConditions });
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getProductsSections],
          queryFn: () => adminLocalizationApi.getProductsSections({ productId: productId })
        });
        showSuccessToast('Saved');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const handleDelete = useCallback(async (question) => {
    try {
      setIsDeleting(true);
      const result = await ConstructorApi.deleteQuestion(question);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getProductsSections],
          queryFn: () => adminLocalizationApi.getProductsSections({ productId: productId })
        });
        showSuccessToast('Deleted');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return {
    handleCreate,
    handleUpdate,
    handleDelete,
    isCreating,
    isUpdating,
    isDeleting
  };
};
